// Pure rotor-disc layout for the PCB stator's magnet rotor (STL emitter lives
// in pcb-stator-rotor-stl.ts). No Angular / DI deps. v1: single disc,
// through-pockets (deliberately stack-ready — an interior rotor in a
// rotor/stator/rotor stack presents poles on both faces through the same
// magnets), plain bore, and a pole-0 index notch as the angular datum for
// magnet insertion and stack phasing. Spec: 2026-07-06 rotor builder v1.
//
// Optional back-face keyed recess (2026-07-09 encoder v0): when a RotorRecessSpec
// is supplied, the disc gains a shallow annular counterbore on its back face for
// the passive encoder target ring to glue into (see pcb-stator-encoder-target).
// The recess is OPTIONAL — with no spec the layout (and the emitted STL) is
// byte-identical to the pre-encoder rotor.

import { MagnetRecommendation, MagnetShape } from './pcb-stator-magnets';
import { POCKET_CLEAR_MM } from './pcb-stator-encoder-bands';

export interface RotorParams {
  magnetShape: MagnetShape;     // which recommendation option to build for
  magnetThicknessMm: number;    // axial magnet thickness (assembly reference)
  discThicknessMm: number;      // disc thickness; flush = magnet thickness
  boreDiameterMm: number;       // plain center bore, as-printed target
  pocketClearanceMm: number;    // press-fit allowance (diametral / per-axis)
}

export type MagnetDims = { dia: number } | { length: number; width: number };

export interface RotorMagnetPlacement {
  cx: number; cy: number;       // center, math frame (mm, y up)
  angleDeg: number;             // pole i at -90 + i*360/count
  polarity: 'N' | 'S';          // alternating, pole 0 = N
  magnetMm: MagnetDims;         // catalog-snapped magnet size
  pocketMm: MagnetDims;         // magnet + clearance
}

/** Back-face keyed recess request. All radii/angles are the TARGET's own
 *  outline (from pcb-stator-encoder-target + pcb-stator-encoder-bands); the
 *  layout applies the fit clearance and the pocket-clearance clamp. */
export interface RotorRecessSpec {
  innerRadiusMm: number;    // target SUBSTRATE inner edge = the target ring's bore radius
                            // (2026-07-13 inner-land fix; was bands.coarse.rInMm — but the
                            // disc substrate extends inward past the copper to its bore, and
                            // an inner annulus resting on un-recessed rotor face holds the
                            // whole disc proud of the recess floor)
  outerRadiusMm: number;    // target disc radius = bands.fine.rOutMm + DISC_RIM_MM (2026-07-13 seating fix)
  targetThicknessMm: number;// target ring thickness (drives recess depth)
  keyAngleRad: number;      // = TargetRing.keyTabAngleRad = keyAzimuthRad(datum, fine):
                            //   half a pole pitch off pole 0 — mid inter-pocket web
  keyWidthMm: number;       // key tab tangential width = KEY_TAB_WIDTH_MM
  keyTabDepthMm: number;    // key tab radial protrusion = KEY_TAB_DEPTH_MM
}

export interface RotorLayout {
  valid: boolean;
  warning?: string;             // set when !valid; first failing rule wins
  discOuterRadiusMm: number;
  boreRadiusMm: number;         // (boreDiameterMm + pocketClearanceMm) / 2
  thicknessMm: number;
  magnetRingRadiusMm: number;   // = rec.meanRadiusMm
  magnets: RotorMagnetPlacement[];
  notch: { angleDeg: number; widthMm: number; depthMm: number };
  /** Innermost radial extent reached by any magnet pocket (min over all pockets
   *  of center-radius minus the pocket's radial half-extent). Through-pockets
   *  leave solid back-face disc material only inboard of this radius — it is
   *  the outer bound for anything (sensor track, recess) that needs that
   *  material. 0 when the layout is invalid. */
  pocketInnerRadiusMm: number;
  /** Back-face target seat, present only when a RotorRecessSpec was supplied
   *  AND it fits (inboard of the pockets, outboard of the bore). depthMm is the
   *  axial counterbore depth; keySlot.depthMm is the RADIAL length of the
   *  outward key finger reaching the target tab tip + fit through the
   *  inter-pocket web (0 only in the honest-drop case, see keyFingerDropped). */
  recess?: {
    rInMm: number; rOutMm: number; depthMm: number;
    keySlot: { angleRad: number; widthMm: number; depthMm: number };
  };
  /** true when a recess exists but the key finger had to be dropped because
   *  even the inter-pocket web can't hold it with POCKET_CLEAR_MM of clearance
   *  (pathologically wide key / crowded pole ring). The target then relies on
   *  glue alone for phase — Task 9's UI must banner this, never silent. */
  keyFingerDropped?: boolean;
  /** true when a RotorRecessSpec WAS supplied but the seat could not fit (inner
   *  wall would swallow the bore, annulus inverted, or depth >= disc thickness) —
   *  the rotor prints SOLID and the target then cannot mount at all. Honest-drop
   *  flag (same convention as keyFingerDropped): the encoder support tier must
   *  banner this red, never let the solid print pass silently (2026-07-13). */
  recessDropped?: boolean;
}

export const ROTOR_RIM_MM = 3;     // rim beyond the pocket ring's outer edge
export const NOTCH_WIDTH_MM = 2;   // tangential
export const NOTCH_DEPTH_MM = 1;   // radial, cut into the rim
export const RECESS_FIT_MM = 0.15; // radial/angular slip fit: recess = target + fit
export const RECESS_GLUE_MM = 0.1; // extra depth over target thickness for glue

export function computeRotorLayout(
  rec: MagnetRecommendation,
  rotor: RotorParams,
  recessSpec?: RotorRecessSpec,
): RotorLayout {
  const base: RotorLayout = {
    valid: false,
    discOuterRadiusMm: 0,
    boreRadiusMm: (rotor.boreDiameterMm + rotor.pocketClearanceMm) / 2,
    thicknessMm: rotor.discThicknessMm,
    magnetRingRadiusMm: rec.meanRadiusMm,
    magnets: [],
    notch: { angleDeg: -90, widthMm: NOTCH_WIDTH_MM, depthMm: NOTCH_DEPTH_MM },
    pocketInnerRadiusMm: 0,
  };

  const opt = rec.options.find(o => o.shape === rotor.magnetShape);
  if (rec.warning || !opt || !opt.standardMm) {
    return { ...base, warning: `No catalog magnet fits — ${rec.warning ?? 'adjust poles/radius'}` };
  }
  if (rec.count % 2 !== 0) {
    return { ...base, warning: 'Odd pole count: N/S alternation needs an even magnet count.' };
  }

  const mag = opt.standardMm;
  const pocket: MagnetDims = 'dia' in mag
    ? { dia: mag.dia + rotor.pocketClearanceMm }
    : { length: mag.length + rotor.pocketClearanceMm, width: mag.width + rotor.pocketClearanceMm };
  const radialHalf = 'dia' in pocket ? pocket.dia / 2 : pocket.length / 2;
  const tangHalf   = 'dia' in pocket ? pocket.dia / 2 : pocket.width  / 2;

  // Neighbor pockets must not touch: centers sit a chord apart (straight-line
  // distance — stricter than the arc pitch, and the physically correct bound).
  // rec.count >= 2 is guaranteed here: count < 2 pairs with rec.warning, which rule 1 intercepts above.
  const chordPitch = 2 * rec.meanRadiusMm * Math.sin(Math.PI / rec.count);
  if (2 * tangHalf >= chordPitch) {
    return { ...base, warning: 'Magnet pockets overlap — fewer poles or smaller magnets.' };
  }
  // Rect inner corners sit at meanR − length/2, where there is less tangential
  // room than the mean-radius chord test sees: the corner's angular half-extent
  // must fit inside the half pole pitch. (Round pockets: chord test is exact.)
  if (!('dia' in pocket) &&
      Math.atan2(pocket.width / 2, rec.meanRadiusMm - pocket.length / 2) >= Math.PI / rec.count) {
    return { ...base, warning: 'Magnet pockets overlap — fewer poles or smaller magnets.' };
  }
  // Rect OUTER corners: discOuterRadiusMm below is sized from the pocket's pure
  // radial reach (meanR + radialHalf) plus ROTOR_RIM_MM — exact for round
  // pockets (isotropic), but a wide rect pocket's outer corner also carries a
  // tangential offset, so its true distance from the disc center is
  // hypot(meanR + radialHalf, tangHalf), which can exceed that radial-only
  // reach once width grows past length by roughly the rim margin. Reject
  // rather than silently emit a pocket whose corner pokes through the disc
  // rim (non-manifold STL) — catalog sizes above 30mm (2026-07-26) made this
  // reachable for the first time at low pole counts.
  if (!('dia' in pocket)) {
    const outerCornerR = Math.hypot(rec.meanRadiusMm + radialHalf, tangHalf);
    const discOuterRadiusForCheck = rec.meanRadiusMm + radialHalf + ROTOR_RIM_MM;
    if (outerCornerR > discOuterRadiusForCheck) {
      return {
        ...base,
        warning: 'Magnet pocket corners reach past the disc rim — fewer poles, a shorter magnet, or a narrower one.',
      };
    }
  }
  if (base.boreRadiusMm >= rec.meanRadiusMm - radialHalf) {
    return { ...base, warning: 'Bore reaches the magnet ring — shrink the bore or enlarge the stator.' };
  }
  if (rotor.discThicknessMm <= 0 || rotor.magnetThicknessMm <= 0 ||
      rotor.boreDiameterMm < 0 || rotor.pocketClearanceMm < 0) {
    return { ...base, warning: 'Rotor dimensions must be positive.' };
  }

  const magnets: RotorMagnetPlacement[] = [];
  for (let i = 0; i < rec.count; i++) {
    const angleDeg = -90 + (i * 360) / rec.count;
    const a = (angleDeg * Math.PI) / 180;
    magnets.push({
      cx: rec.meanRadiusMm * Math.cos(a),
      cy: rec.meanRadiusMm * Math.sin(a),
      angleDeg,
      polarity: i % 2 === 0 ? 'N' : 'S',
      magnetMm: mag,
      pocketMm: pocket,
    });
  }

  // Innermost radial extent of any pocket: for a round pocket the closest
  // point to the disc center is exactly meanR - radialHalf. For a rect
  // pocket the rectangle is rotated so its own radial axis lies on the ray
  // through its center — the disc center falls on that same ray, extended
  // through the origin — so its closest point is also exactly the inner
  // corner at meanR - radialHalf. Both shapes reduce to the same formula;
  // compute per-magnet (not from meanR directly) so this stays correct if
  // placements ever stop sharing a single ring radius.
  const pocketInnerRadiusMm = Math.min(
    ...magnets.map(m => {
      const half = 'dia' in m.pocketMm ? m.pocketMm.dia / 2 : m.pocketMm.length / 2;
      return Math.hypot(m.cx, m.cy) - half;
    }),
  );

  const discOuterRadiusMm = rec.meanRadiusMm + radialHalf + ROTOR_RIM_MM;

  const { recess, keyFingerDropped, recessDropped } =
    computeRecess(recessSpec, base.boreRadiusMm, pocketInnerRadiusMm, rotor.discThicknessMm, discOuterRadiusMm, magnets);

  return {
    ...base,
    valid: true,
    discOuterRadiusMm,
    magnets,
    pocketInnerRadiusMm,
    recess,
    keyFingerDropped,
    recessDropped,
  };
}

/** Derives the back-face recess from the target spec: an annulus bracketing the
 *  target copper (± fit), with its OUTER wall clamped to keep POCKET_CLEAR_MM
 *  off the through-pockets — the recess floor sits at depth from the back face
 *  and must never break into a pocket. The target's key tab protrudes radially
 *  beyond the disc, so the mating slot is an OUTWARD finger at the key azimuth
 *  (keySlot) reaching the tab tip + fit. The azimuth sits mid inter-pocket web
 *  (keyAzimuthRad), so the finger is NOT capped to the annulus clamp — instead
 *  its polygon is distance-checked against every pocket placement and honestly
 *  dropped (keyFingerDropped) if even the web can't hold POCKET_CLEAR_MM, or if
 *  the finger tip would poke past the rotor disc's own outer edge (no material
 *  there to form it). Returns undefined recess when no spec was given; when a
 *  spec WAS given but the seat cannot fit (inner wall would swallow the bore,
 *  annulus inverted, or depth >= disc thickness) the recess is dropped WITH the
 *  recessDropped flag — the rotor prints solid and the target cannot mount, so
 *  the drop must reach the UI tier, never pass silently (2026-07-13). */
function computeRecess(
  spec: RotorRecessSpec | undefined,
  boreRadiusMm: number,
  pocketInnerRadiusMm: number,
  discThicknessMm: number,
  discOuterRadiusMm: number,
  magnets: RotorMagnetPlacement[],
): { recess: RotorLayout['recess']; keyFingerDropped?: boolean; recessDropped?: boolean } {
  if (!spec) return { recess: undefined };

  const pocketCap = pocketInnerRadiusMm - POCKET_CLEAR_MM;
  const rInMm = spec.innerRadiusMm - RECESS_FIT_MM;
  const rOutMm = Math.min(spec.outerRadiusMm + RECESS_FIT_MM, pocketCap);
  const depthMm = spec.targetThicknessMm + RECESS_GLUE_MM;

  const fits = rInMm > boreRadiusMm && rOutMm > rInMm && depthMm > 0 && depthMm < discThicknessMm;
  if (!fits) return { recess: undefined, recessDropped: true };

  // Outward key finger through the web: full tab mate rule, no pocket-cap clamp.
  const fingerOuter = spec.outerRadiusMm + spec.keyTabDepthMm + RECESS_FIT_MM;
  const widthMm = spec.keyWidthMm + 2 * RECESS_FIT_MM;
  const keyRadial = Math.max(0, fingerOuter - rOutMm);
  const fingerOk = keyRadial > 0 &&
    widthMm / 2 < rOutMm &&                 // asin domain: base corners must land on the rOut circle
    fingerOuter < discOuterRadiusMm &&      // finger tip must stay on the disc (no material beyond its rim)
    fingerClearanceMm(rOutMm, fingerOuter, spec.keyAngleRad, widthMm, magnets) >= POCKET_CLEAR_MM;

  return {
    recess: {
      rInMm, rOutMm, depthMm,
      keySlot: { angleRad: spec.keyAngleRad, widthMm, depthMm: fingerOk ? keyRadial : 0 },
    },
    keyFingerDropped: fingerOk ? undefined : true,
  };
}

// ── key-finger / pocket clearance (exact 2D distances, not angular bounds) ──

type XY = [number, number];

function distPointSeg([px, py]: XY, [ax, ay]: XY, [bx, by]: XY): number {
  const abx = bx - ax, aby = by - ay;
  const len2 = abx * abx + aby * aby;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / len2));
  return Math.hypot(px - (ax + t * abx), py - (ay + t * aby));
}

function segsIntersect(p1: XY, p2: XY, q1: XY, q2: XY): boolean {
  const cross = (o: XY, a: XY, b: XY) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const d1 = cross(q1, q2, p1), d2 = cross(q1, q2, p2);
  const d3 = cross(p1, p2, q1), d4 = cross(p1, p2, q2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function distSegSeg(p1: XY, p2: XY, q1: XY, q2: XY): number {
  if (segsIntersect(p1, p2, q1, q2)) return 0;
  return Math.min(
    distPointSeg(p1, q1, q2), distPointSeg(p2, q1, q2),
    distPointSeg(q1, p1, p2), distPointSeg(q2, p1, p2),
  );
}

/** Min planar distance from the key-finger trapezoid (same construction the STL
 *  emitter uses: base corners on the rOut circle at ±asin(halfW/rOut), tip
 *  corners at fingerOuter on the same rays) to any magnet pocket. */
function fingerClearanceMm(
  rOutMm: number, fingerOuterMm: number, angleRad: number, widthMm: number,
  magnets: RotorMagnetPlacement[],
): number {
  const angHalf = Math.asin((widthMm / 2) / rOutMm);
  const angles = [angleRad - angHalf, angleRad + angHalf];
  const corners: XY[] = [
    [rOutMm * Math.cos(angles[0]), rOutMm * Math.sin(angles[0])],
    [fingerOuterMm * Math.cos(angles[0]), fingerOuterMm * Math.sin(angles[0])],
    [fingerOuterMm * Math.cos(angles[1]), fingerOuterMm * Math.sin(angles[1])],
    [rOutMm * Math.cos(angles[1]), rOutMm * Math.sin(angles[1])],
  ];
  const edges: Array<[XY, XY]> = corners.map((c, i) => [c, corners[(i + 1) % corners.length]]);

  let min = Infinity;
  for (const m of magnets) {
    if ('dia' in m.pocketMm) {
      const r = m.pocketMm.dia / 2;
      for (const [a, b] of edges) min = Math.min(min, distPointSeg([m.cx, m.cy], a, b) - r);
    } else {
      const rot = (m.angleDeg * Math.PI) / 180;
      const ca = Math.cos(rot), sa = Math.sin(rot);
      const hl = m.pocketMm.length / 2, hw = m.pocketMm.width / 2;
      const rc: XY[] = ([[-hl, -hw], [hl, -hw], [hl, hw], [-hl, hw]] as XY[])
        .map(([x, y]) => [m.cx + x * ca - y * sa, m.cy + x * sa + y * ca] as XY);
      for (const [a, b] of edges) {
        for (let i = 0; i < rc.length; i++) {
          min = Math.min(min, distSegSeg(a, b, rc[i], rc[(i + 1) % rc.length]));
        }
      }
    }
  }
  return min;
}

